import React from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, ShieldCheck, UserCircle } from 'lucide-react';
import { authService } from '../services/authService';
import { User } from '../types/auth';
import DismissibleError from './DismissibleError';

interface ProfilePageProps {
  user: User;
  onPasswordChanged: () => Promise<void> | void;
}

type FieldErrors = Record<string, string[]>;

const value = (input: unknown, fallback = '-'): string =>
  input === null || input === undefined || input === '' ? fallback : String(input);

const fullName = (user: User): string =>
  [user.first_name, user.last_name].filter(Boolean).join(' ') || '-';

const collectErrors = (error: any): FieldErrors => {
  const data = error?.originalError?.response?.data || error?.response?.data;
  const errors = data?.errors || data;
  if (!errors || typeof errors !== 'object') return {};
  return Object.entries(errors).reduce<FieldErrors>((current, [field, messages]) => {
    current[field] = Array.isArray(messages) ? messages.map(String) : [String(messages)];
    return current;
  }, {});
};

const errorText = (fieldErrors: FieldErrors): string[] =>
  Object.entries(fieldErrors).flatMap(([field, messages]) =>
    messages.map((message) => `${field === 'non_field_errors' ? 'Password' : field.replace(/_/g, ' ')}: ${message}`)
  );

const ProfilePage: React.FC<ProfilePageProps> = ({ user, onPasswordChanged }) => {
  const [profile, setProfile] = React.useState<User>(user);
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({});
  const navigate = useNavigate();

  React.useEffect(() => {
    let mounted = true;
    authService.getProfile()
      .then((response) => {
        if (mounted && response.user) setProfile(response.user);
      })
      .catch(() => {
        if (mounted) setErrorMessage('Profile details are temporarily unavailable.');
      });
    return () => { mounted = false; };
  }, []);

  const departments = profile.departments || [];
  const primaryDepartment = profile.primary_department;

  const resetMessages = () => {
    setSuccessMessage('');
    setErrorMessage('');
    setFieldErrors({});
  };

  const submitPasswordChange = async (event: React.FormEvent) => {
    event.preventDefault();
    resetMessages();
    const nextErrors: FieldErrors = {};
    if (!currentPassword) nextErrors.old_password = ['Current password is required.'];
    if (!newPassword) nextErrors.new_password = ['New password is required.'];
    if (!confirmPassword) nextErrors.confirm_password = ['Confirm new password is required.'];
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      nextErrors.confirm_password = ['New password and confirmation must match.'];
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setErrorMessage('Password change failed.');
      return;
    }

    setLoading(true);
    try {
      const response = await authService.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccessMessage(response.message || 'Password changed successfully. Please log in again.');
      await onPasswordChanged();
      navigate('/login', { replace: true });
    } catch (error: any) {
      const nextFieldErrors = collectErrors(error);
      setFieldErrors(nextFieldErrors);
      setErrorMessage(error?.message || 'Password change failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading">
        <div>
          <h1>My Profile</h1>
          <p>Review your account details and change your own password.</p>
        </div>
      </div>

      {successMessage && <div className="success-message">{successMessage}</div>}
      {errorMessage && (
        <>
          <DismissibleError
            message={errorMessage}
            onDismiss={() => {
              setErrorMessage('');
              setFieldErrors({});
            }}
          />
          {errorText(fieldErrors).length > 0 && (
            <div className="error-message" role="alert">
              <span className="error-body">{errorText(fieldErrors).join(' ')}</span>
            </div>
          )}
        </>
      )}

      <div className="dashboard-grid">
        <section className="panel">
          <div className="section-heading"><h2>Account Information</h2><UserCircle size={18} /></div>
          <div className="detail-grid governance-detail-grid">
            <div><span>Username</span><strong>{value(profile.username)}</strong></div>
            <div><span>Email</span><strong>{value(profile.email)}</strong></div>
            <div><span>Full name</span><strong>{fullName(profile)}</strong></div>
            <div><span>Account status</span><strong>{profile.is_active ? 'Active' : 'Inactive'}</strong></div>
          </div>
        </section>

        <section className="panel">
          <div className="section-heading"><h2>Role and Departments</h2><ShieldCheck size={18} /></div>
          <div className="detail-grid governance-detail-grid">
            <div><span>Role</span><strong>{value(profile.role_display || profile.role_code)}</strong></div>
            <div><span>Primary department</span><strong>{value(primaryDepartment?.name)}</strong></div>
            <div className="full-width">
              <span>Assigned departments</span>
              <strong>{departments.length ? departments.map((department) => department.name).join(', ') : '-'}</strong>
            </div>
          </div>
        </section>
      </div>

      <section className="panel">
        <div className="section-heading"><h2>Change Password</h2><KeyRound size={18} /></div>
        <p className="form-helper">Forgot your current password? Log out and use "Forgot password?" from the login page.</p>
        <form className="form-grid" onSubmit={submitPasswordChange}>
          <label>
            Current password
            <input
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
            />
          </label>
          <label>
            New password
            <input
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
            />
          </label>
          <label>
            Confirm new password
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
            />
          </label>
          <p className="form-helper full-width">Use at least 12 characters with uppercase, lowercase, number, and special character.</p>
          <div className="form-actions full-width">
            <button className="btn btn-primary" type="submit" disabled={loading}>
              <KeyRound size={14} />{loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default ProfilePage;
